import React, { useState, useEffect } from "react";
import {
  Dropdown,
  DropdownButton,
  Modal,
  Button,
  Form,
  Alert,
  ProgressBar,
  Spinner,
} from "react-bootstrap";

const UploadButton = ({
  onSaveUrl,
  onSaveText,
  onSaveFile,
  editUrl,
  editTextFile,
  setEditUrlIndex,
  setEditTextIndex,
}) => {
  const [showUrlModal, setShowUrlModalState] = useState(false);
  const [showTextModal, setShowTextModalState] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [webUrl, setWebUrl] = useState("");
  const [textFileName, setTextFileName] = useState("");
  const [textContent, setTextContent] = useState("");
  const [files, setFiles] = useState([]);
  const [uploadError, setUploadError] = useState({});
  const [error, setError] = useState("");
  const [isSavingUrl, setIsSavingUrl] = useState(false); // State to track saving URL
  const [isSavingText, setIsSavingText] = useState(false); // State to track saving text
  const [isSavingFiles, setIsSavingFiles] = useState(false); // State to track saving files

  useEffect(() => {
    if (editUrl !== undefined) {
      setWebUrl(editUrl.url); // Set the `url` property of the `editUrl` object
      setShowUrlModalState(true);
    }
  }, [editUrl]);

  useEffect(() => {
    if (editTextFile !== undefined) {
      setTextFileName(editTextFile.name);
      setTextContent(editTextFile.content);
      setShowTextModalState(true);
    }
  }, [editTextFile]);

  const handleSelect = (eventKey) => {
    if (eventKey === "1") {
      setWebUrl(""); // Clear the web URL input field for a new upload
      setShowUrlModalState(true);
    } else if (eventKey === "2") {
      setShowUploadModal(true);
    } else if (eventKey === "3") {
      setTextFileName(""); // Clear the text file name input field for a new upload
      setTextContent(""); // Clear the text content input field for a new upload
      setShowTextModalState(true);
    }
    console.log(`Selected: ${eventKey}`);
  };

  const handleCloseUrlModal = () => {
    setShowUrlModalState(false);
    setEditUrlIndex(null);
    setError("");
  };

  const handleCloseTextModal = () => {
    setShowTextModalState(false);
    setEditTextIndex(null);
    setError("");
  };

  const handleCloseUploadModal = () => {
    setShowUploadModal(false);
    setFiles([]);
    setUploadError({});
  };

  const handleUrlInputChange = (e) => {
    if (e.target.value.length > 70) {
      setError("URL cannot exceed 70 characters."); // Set error message if limit is exceeded
    } else {
      setError(""); // Clear error message if within limit
      setWebUrl(e.target.value);
    }
  };
  const handleTextFileNameChange = (e) => setTextFileName(e.target.value);
  const handleTextContentChange = (e) => {
    if (e.target.value.length <= 400) {
      setTextContent(e.target.value);
    }
  };

  const validateUrl = (url) => {
    const urlPattern = /^(https?:\/\/)/;
    return urlPattern.test(url);
  };

  const handleSaveUrl = async () => {
    if (!validateUrl(webUrl)) {
      setError("Please enter a valid URL starting with http:// or https://.");
      return;
    }
    setIsSavingUrl(true); // Show spinner while saving URL
    const isDuplicate = await onSaveUrl(webUrl); // Await the result of onSaveUrl
    if (isDuplicate) {
      setError(
        "This URL already exists in this file. Please enter a different URL."
      );
      setIsSavingUrl(false); // Hide spinner if duplicate
      return;
    }
    setShowUrlModalState(false);
    setError(""); // Clear error message
    setIsSavingUrl(false); // Hide spinner after saving
  };

  const handleSaveText = () => {
    if (!textFileName.trim() || !textContent.trim()) {
      setError("Please enter both file name and content.");
      return;
    }
    setIsSavingText(true); // Show spinner while saving text
    onSaveText({ name: textFileName, content: textContent }); // Call parent function to save or update the text file
    setShowTextModalState(false);
    setError("");
    setIsSavingText(false); // Hide spinner after saving
  };

  const handleSaveFiles = () => {
    setIsSavingFiles(true); // Show spinner while saving files
    const actualFiles = files.map((file) => file.actualFile); // Extract actual file objects
    onSaveFile(actualFiles); // Pass files to the parent component
    setShowUploadModal(false);
    setFiles([]);
    setUploadError({});
    setIsSavingFiles(false); // Hide spinner after saving
  };

  const allowedFileTypes = [
    "application/pdf",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document", // .docx
    "application/msword", // .doc
    "text/plain", // .txt
    "application/xml", // .xml
    "text/xml", // .xml (alternative MIME type)
    "text/html", // .html
    "text/markdown", // .md
    "text/x-markdown", // .md (alternative MIME type for some systems)
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", // .xlsx
    "application/vnd.ms-excel", // .xls
    "image/jpeg", // .jpg, .jpeg
    "image/png", // .png
    "application/vnd.ms-powerpoint", // .ppt
    "application/vnd.openxmlformats-officedocument.presentationml.presentation", // .pptx
  ];

  const allowedFileExtensions = [".md"]; // Add file extensions as a fallback
  const handleFileChange = (event) => {
    const selectedFiles = Array.from(event.target.files);

    // Calculate total size including existing files
    const totalSize = [...files, ...selectedFiles].reduce(
      (acc, file) => acc + file.size,
      0
    );

    if (totalSize > 50 * 1024 * 1024) {
      // Check if total size exceeds 50MB
      setError(
        "Total file size exceeds 50MB limit. Please select smaller files."
      );
      return; // Exit early to prevent adding files
    }

    const newFiles = selectedFiles.map((file) => {
      const fileExtension = file.name
        .slice(file.name.lastIndexOf("."))
        .toLowerCase();
      if (fileExtension === ".md") {
        file = new File([file], file.name, { type: "text/markdown" }); // Override MIME type
      }

      if (
        !allowedFileTypes.includes(file.type) &&
        !allowedFileExtensions.includes(fileExtension)
      ) {
        setUploadError((prev) => ({
          ...prev,
          [file.name]: "Invalid file type. Upload only supported formats",
        }));
        return {
          name: file.name,
          size: (file.size / 1024 / 1024).toFixed(1),
          status: "failed",
          actualFile: file,
        };
      }

      return {
        name: file.name,
        size: (file.size / 1024 / 1024).toFixed(1),
        status: "pending",
        actualFile: file,
      };
    });

    setFiles((prevFiles) => [...prevFiles, ...newFiles]); // Include all files, even failed ones
    if (newFiles.every((file) => file.status !== "failed")) {
      setError(""); // Clear error message only if all files are valid
    }
  };

  const handleDrop = (event) => {
    event.preventDefault();
    const droppedFiles = Array.from(event.dataTransfer.files);
    const totalSize = [...files, ...droppedFiles].reduce(
      (acc, file) => acc + file.size,
      0
    );

    if (totalSize > 50 * 1024 * 1024) {
      // Check if total size exceeds 50MB
      setError(
        "Total file size exceeds 50MB limit. Please select smaller files."
      );
      return;
    }

    const newFiles = droppedFiles.map((file) => {
      if (!allowedFileTypes.includes(file.type)) {
        setUploadError((prev) => ({
          ...prev,
          [file.name]: "Invalid file type. Upload only supported formats",
        }));
        return {
          name: file.name,
          size: (file.size / 1024 / 1024).toFixed(1),
          status: "failed",
          actualFile: file,
        };
      }
      return {
        name: file.name,
        size: (file.size / 1024 / 1024).toFixed(1),
        status: "pending",
        actualFile: file,
      };
    });

    setFiles((prevFiles) => [...prevFiles, ...newFiles]); // Include all files, even failed ones
    setError(""); // Clear error message if files are valid
  };

  const handleDragOver = (event) => {
    event.preventDefault();
  };

  const removeFile = (fileName) => {
    setFiles(files.filter((file) => file.name !== fileName));
    setUploadError((prev) => {
      const updatedErrors = { ...prev };
      delete updatedErrors[fileName];
      return updatedErrors;
    });
    document.getElementById("fileUploadInput").value = ""; // Reset the file input value
  };

  const hasInvalidFiles = files.some((file) => file.status === "failed"); // Check if there are any invalid files

  return (
    <>
      <DropdownButton
        id="dropdown-basic-button"
        title={
          <span className="d-flex align-items-center fw-bold">
            <img src="/assets/plus-blue.svg" alt="Info" className="me-2" />{" "}
            Upload
          </span>
        }
        onSelect={handleSelect}
        className="upload-btn-dropdown"
      >
        <Dropdown.Item eventKey="1" className="pb-3 d-flex align-items-center">
          <img src="/assets/link.svg" alt="Info" className="me-2 bg-icon" />
          <div className="d-flex flex-column">
            <span className="fw-bold">Add Web Pages</span>
            <small>Upload website link</small>
          </div>
        </Dropdown.Item>
        <Dropdown.Item eventKey="2" className="pb-3 d-flex align-items-center">
          <img src="/assets/upload.svg" alt="Info" className="me-2 bg-icon" />
          <div className="d-flex flex-column">
            <span className="fw-bold">Upload Files</span>
            <small>Files should be less than 50MB.</small>
          </div>
        </Dropdown.Item>
        <Dropdown.Item eventKey="3" className="pb-3 d-flex align-items-center">
          <img
            src="/assets/file-text.svg"
            alt="Info"
            className="me-2 bg-icon"
          />
          <div className="d-flex flex-column">
            <span className="fw-bold">Add Texts</span>
            <small>Enter contents manually.</small>
          </div>
        </Dropdown.Item>
      </DropdownButton>

      {/* URL Modal */}
      <Modal
        show={showUrlModal}
        onHide={handleCloseUrlModal}
        centered
        size="lg"
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            handleSaveUrl();
          }
        }}
      >
        <Modal.Header closeButton>
          <Modal.Title className="fs-three">
            {editUrl !== undefined ? "Edit Web Page" : "Add Web Page"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form className="py-2 pb-5">
            <Form.Group controlId="formWebUrl">
              <Form.Label>URL Address</Form.Label>
              <Form.Control
                type="url"
                placeholder="Enter URL"
                value={webUrl}
                onChange={handleUrlInputChange}
              />

              {error && (
                <Alert variant="danger" className="mt-2 p-2">
                  {error}
                </Alert>
              )}
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="none"
            className="fw-bold btn btn-light"
            onClick={handleCloseUrlModal}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            className="ms-2 bg-purple-color fw-bold text-white btn btn-none"
            onClick={handleSaveUrl}
            disabled={isSavingUrl} // Disable button while saving URL
          >
            {isSavingUrl ? (
              <Spinner
                as="span"
                animation="border"
                size="sm"
                role="status"
                aria-hidden="true"
              />
            ) : editUrl !== undefined ? (
              "Update"
            ) : (
              "Save"
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Text Modal */}
      <Modal
        show={showTextModal}
        onHide={handleCloseTextModal}
        centered
        size="lg"
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            handleSaveText();
          }
        }}
      >
        <Modal.Header closeButton>
          <Modal.Title className="fs-three">
            {editTextFile !== undefined ? "Edit Text" : "Add Text"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form className="py-2 pb-5">
            <Form.Group controlId="formTextFileName">
              <Form.Label>Text File Name</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter file name"
                value={textFileName}
                onChange={handleTextFileNameChange}
              />
            </Form.Group>
            <Form.Group controlId="formTextContent" className="mt-3">
              <Form.Label>Text Context</Form.Label>
              <Form.Control
                as="textarea"
                rows={6}
                placeholder="Enter text context..."
                value={textContent}
                onChange={handleTextContentChange}
              />
              <div className="text-muted mt-1">
                {textContent.length}/400 characters
              </div>
              {error && (
                <Alert variant="danger" className="mt-2 p-2">
                  {error}
                </Alert>
              )}
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="none"
            className="fw-bold btn btn-light"
            onClick={handleCloseTextModal}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            className="ms-2 bg-purple-color fw-bold text-white btn btn-none"
            onClick={handleSaveText}
            disabled={isSavingText} // Disable button while saving text
          >
            {isSavingText ? (
              <Spinner
                as="span"
                animation="border"
                size="sm"
                role="status"
                aria-hidden="true"
              />
            ) : editTextFile !== undefined ? (
              "Update"
            ) : (
              "Save"
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Upload Modal */}
      <Modal
        show={showUploadModal}
        onHide={handleCloseUploadModal}
        centered
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title className="fs-three">Upload File</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {/* Drag & Drop Box */}
          <div
            className="border-dashed-upload my-3 p-4 text-center rounded bg-light-avg"
            onDrop={handleDrop}
            onDragOver={handleDragOver}
          >
            <img
              src="../assets/upload-icon.svg"
              alt="Knowledge Base"
              className="nav-icon"
            />
            <p className="my-3 fw-bold fs-three"> Drag & drop files here</p>
            <p className="text-secondary">
              or Click on{" "}
              <span className="purple-color fw-bold">
                {" "}
                <input
                  type="file"
                  multiple
                  className="d-none"
                  id="fileUploadInput"
                  onChange={handleFileChange}
                />
                <label htmlFor="fileUploadInput" className="cursor-pointer">
                  Browse
                </label>
              </span>
            </p>

            <small className="text-muted">
              Files should be less than 50MB in total.
              <br />
              Supported formats: .PDF, .DOCX, .DOC, .TXT, .XML, .HTML, .MD,
              .XLSX, .XLS, .JPG, .JPEG, .PNG, .PPT, .PPTX
            </small>
          </div>
          {error && (
            <Alert variant="danger" className="mt-2 p-2">
              {error}
            </Alert>
          )}
          {/* File Upload List */}
          <div className="my-5">
            <p className="fw-bold">Files uploaded ({files.length})</p>
            {files.map((file, index) => (
              <div
                key={index}
                className="uploaded-file py-4 bg-light-subtle d-flex align-items-center justify-content-between p-2 border rounded mb-2"
              >
                <div>
                  <div className="d-flex align-items-center">
                    <img
                      src="../assets/file-text-pdf.svg"
                      alt="Knowledge Base"
                      className="bg-icon me-2"
                    />
                    <div>
                      <span className="fw-bold gray-color">{file.name}</span>
                      <div className="text-muted small">
                        {file.size} MB • Uploaded
                      </div>
                    </div>
                  </div>
                  {file.status === "uploading" && (
                    <ProgressBar animated now={50} className="my-1" />
                  )}
                  {file.status === "failed" && (
                    <Alert
                      variant="danger"
                      className="mt-2 p-2 w-100 small mb-0 alert-error"
                    >
                      {uploadError[file.name]}
                    </Alert>
                  )}
                </div>

                <img
                  src="../assets/trash-1.svg"
                  alt="Delete"
                  className="me-2"
                  title="Delete"
                  onClick={() => removeFile(file.name)}
                  style={{ cursor: "pointer" }}
                />
              </div>
            ))}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="none"
            className="fw-bold btn btn-light"
            onClick={handleCloseUploadModal}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            className="ms-2 bg-purple-color fw-bold text-white btn btn-none"
            onClick={handleSaveFiles}
            disabled={files.length === 0 || hasInvalidFiles || isSavingFiles} // Disable if there are invalid files
          >
            {isSavingFiles ? (
              <Spinner
                as="span"
                animation="border"
                size="sm"
                role="status"
                aria-hidden="true"
              />
            ) : (
              "Save"
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default UploadButton;
